'use client';

import Link from 'next/link';
import { formatLongDate } from '@/app/utils/date.utils';
import { useRef, useEffect, useState } from 'react';

export default function FeatureTimeline({ features, currentSlug }) {
    const activeRef = useRef(null);
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (activeRef.current && containerRef.current) {
            const container = containerRef.current;
            const active = activeRef.current;
            const containerTop = container.getBoundingClientRect().top;
            const activeTop = active.getBoundingClientRect().top;
            const scrollTop =
                activeTop -
                containerTop +
                container.scrollTop -
                container.clientHeight / 2 +
                active.offsetHeight / 2;
            container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
    }, [currentSlug]);

    if (!features?.length) return null;

    const currentFeature = features.find((f) => f.slug === currentSlug);

    return (
        <>
            {/* Mobile: sticky bar + collapsible dropdown */}
            <div className="sticky top-0 z-20 bg-white md:hidden">
                <button
                    className="flex w-full items-center justify-between border-b border-gray-200 px-4 py-3"
                    onClick={() => setIsOpen((o) => !o)}
                    aria-expanded={isOpen}>
                    <div className="min-w-0 text-left">
                        <span className="block text-xs text-gray-400">Features</span>
                        <span className="block truncate text-sm font-medium text-gray-900">
                            {currentFeature?.title ?? 'Browse features'}
                        </span>
                    </div>
                    <svg
                        className={`ml-2 size-4 shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                {isOpen && (
                    <div className="max-h-64 overflow-y-auto border-b border-gray-200 bg-white shadow-md">
                        {features.map((feature, index) => {
                            const isActive = feature.slug === currentSlug;
                            return (
                                <Link
                                    key={feature.identifier || index}
                                    href={`/features/${feature.slug}`}
                                    onClick={() => setIsOpen(false)}
                                    className={`relative block px-5 py-3 transition-colors hover:bg-gray-100 ${
                                        isActive
                                            ? 'border-l-2 border-primary bg-gray-100 text-primary'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}>
                                    <span
                                        className={`block text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>
                                        {feature.title}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-gray-400">
                                        {formatLongDate(feature.releaseDate)}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Desktop: sticky sidebar */}
            <nav
                ref={containerRef}
                className="sticky top-0 hidden h-screen overflow-y-auto border-r border-gray-200 py-6 md:block"
                style={{ scrollbarWidth: 'thin' }}>
                {features.map((feature, index) => {
                    const isActive = feature.slug === currentSlug;
                    return (
                        <Link
                            key={feature.identifier || index}
                            href={`/features/${feature.slug}`}
                            ref={isActive ? activeRef : null}
                            className={`relative block px-5 py-3 transition-colors hover:bg-gray-100 ${
                                isActive
                                    ? 'border-r-2 border-primary bg-gray-100 text-primary'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}>
                            <span
                                className={`block text-sm ${isActive ? 'font-bold' : 'font-medium'}`}>
                                {feature.title}
                            </span>
                            <span className="mt-0.5 block text-xs text-gray-400">
                                {formatLongDate(feature.releaseDate)}
                            </span>
                        </Link>
                    );
                })}
            </nav>
        </>
    );
}
